import { useCallback, useEffect, useReducer, useRef } from 'react'

import { AdministracaoService } from '@core/services'
import { CarometroAluno } from '@core/types/services'

type PhotoStatus = 'idle' | 'loading' | 'loaded' | 'not-found' | 'error'

type PhotoCacheEntry = {
  status: PhotoStatus
  objectUrl?: string
  promise?: Promise<void>
}

const getAlunoInlinePhoto = (aluno?: CarometroAluno | null) => {
  const photo = aluno?.foto ?? aluno?.fotoUrl ?? aluno?.foto_url

  if (typeof photo === 'string' && photo.startsWith('data:')) {
    return photo
  }

  return null
}

const getAlunoPhotoKey = (aluno?: CarometroAluno | null) => {
  const pessoaId = aluno?.pessoaId ?? aluno?.pessoa_id ?? aluno?.id

  if (pessoaId === undefined || pessoaId === null || pessoaId === '') {
    return null
  }

  return String(pessoaId)
}

const isValidImageBlob = (blob?: Blob) => {
  return Boolean(blob && blob.size > 0 && blob.type.startsWith('image/'))
}

export const useCarometroPhotoCache = (
  alunos: CarometroAluno[],
  selectedAluno?: CarometroAluno | null
) => {
  const cacheRef = useRef<Map<string, PhotoCacheEntry>>(new Map())
  const [, forceUpdate] = useReducer((value: number) => value + 1, 0)

  const ensurePhotoLoaded = useCallback((aluno?: CarometroAluno | null) => {
    if (!aluno || getAlunoInlinePhoto(aluno)) return

    const key = getAlunoPhotoKey(aluno)

    if (!key) return

    const numericPessoaId = Number(key)

    if (Number.isNaN(numericPessoaId)) return

    const cachedEntry = cacheRef.current.get(key)

    if (
      cachedEntry?.status === 'loaded' ||
      cachedEntry?.status === 'not-found' ||
      cachedEntry?.status === 'error' ||
      cachedEntry?.promise
    ) {
      return
    }

    const promise = (async () => {
      try {
        const blob = await AdministracaoService.obterFotoCarometroAluno({
          pessoaId: numericPessoaId
        })

        if (!blob) {
          cacheRef.current.set(key, { status: 'not-found' })
          forceUpdate()
          return
        }

        if (!isValidImageBlob(blob)) {
          cacheRef.current.set(key, { status: 'error' })
          forceUpdate()
          return
        }

        const objectUrl = URL.createObjectURL(blob)

        cacheRef.current.set(key, {
          status: 'loaded',
          objectUrl
        })
        forceUpdate()
      } catch {
        cacheRef.current.set(key, { status: 'error' })
        forceUpdate()
      }
    })()

    cacheRef.current.set(key, {
      status: 'loading',
      promise
    })
  }, [])

  const getPhotoSrc = useCallback((aluno?: CarometroAluno | null) => {
    const inlinePhoto = getAlunoInlinePhoto(aluno)

    if (inlinePhoto) return inlinePhoto

    const key = getAlunoPhotoKey(aluno)

    if (!key) return null

    return cacheRef.current.get(key)?.objectUrl ?? null
  }, [])

  useEffect(() => {
    alunos.forEach(aluno => ensurePhotoLoaded(aluno))
  }, [alunos, ensurePhotoLoaded])

  useEffect(() => {
    ensurePhotoLoaded(selectedAluno)
  }, [ensurePhotoLoaded, selectedAluno])

  useEffect(() => {
    return () => {
      cacheRef.current.forEach(entry => {
        if (entry.objectUrl) {
          URL.revokeObjectURL(entry.objectUrl)
        }
      })
      cacheRef.current.clear()
    }
  }, [])

  return {
    getPhotoSrc
  }
}
